import React, { useEffect, useState } from 'react';
import { ArrowsClockwise, Lightning, PushPin, PushPinSlash, WarningCircle, XCircle } from '@phosphor-icons/react';
import DateTimePickerField from '../../../shared/components/ui/DateTimePickerField.jsx';
import Alerts from '../../../shared/components/ui/Alerts.jsx';
import { getUserFromToken } from '../../../context/authContext.jsx';
import DynamicForm from '../../forms/components/DynamicForm.jsx';
import { taskService } from '../../../services/tasks/taskService.js';
import { buildFixedTeamPayload, taskHasTeam } from '../utils/taskFixedTeam.js';

function buildResetPayload(task) {
  return {
    ...task,
    status: 'pendiente',
    taskType: task?.taskType || task?.taskTypeId,
    teamId: task?.teamId || task?.team?.id || null,
    client: {
      ...(task?.client || {})
    }
  };
}

function getDefaultReactivationReason() {
  const currentUser = getUserFromToken();
  const requesterName = currentUser?.name;
  return requesterName ? `Reactivación solicitada por ${requesterName}` : 'Reactivación solicitada';
}

function isBeforeToday(dateValue) {
  if (!dateValue) {
    return false;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  const selectedDay = new Date(parsedDate);
  selectedDay.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return selectedDay.getTime() < today.getTime();
}

function TaskQuickActionsCard({ task, onActionComplete }) {
  const [reactivationReason, setReactivationReason] = useState('');
  const [reactivationDate, setReactivationDate] = useState('');
  const [loadingAction, setLoadingAction] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showForceCreateAlert, setShowForceCreateAlert] = useState(false);

  const isCancelled = task?.status === 'cancelada';
  const isFinalized = task?.status === 'finalizada';
  const isFixedTeam = task?.fixedTeam === true;
  const hasTeam = taskHasTeam(task);
  const canEditReactivationFields = isCancelled && loadingAction === '' && !showForceCreateAlert;
  const minReactivationDate = new Date();
  minReactivationDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (isCancelled) {
      setReactivationReason(getDefaultReactivationReason());
      return;
    }

    setReactivationReason('');
    setShowForceCreateAlert(false);
  }, [task?.id, isCancelled]);

  const clearMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleReset = async () => {
    clearMessages();
    setShowForceCreateAlert(false);

    if (isCancelled || isFinalized) {
      setErrorMessage('No se puede reiniciar una tarea cancelada o finalizada.');
      return;
    }
    setLoadingAction('reset');

    try {
      const resetPayload = buildResetPayload(task);
      await taskService.updateTask(task.id, resetPayload);

      setSuccessMessage('La tarea se reinició y pasó a Pendiente.');
      await onActionComplete?.();
    } catch (error) {
      setErrorMessage(error.message || 'No se pudo reiniciar la tarea.');
    } finally {
      setLoadingAction('');
    }
  };

  const handleToggleFixedTeam = async () => {
    clearMessages();
    setShowForceCreateAlert(false);

    if (!hasTeam) {
      setErrorMessage('La tarea debe tener un equipo asignado para fijarla.');
      return;
    }
    setLoadingAction('fixedTeam');

    try {
      await taskService.updateTask(task.id, buildFixedTeamPayload(task, !isFixedTeam));

      setSuccessMessage(isFixedTeam ? 'La tarea se desfijó del equipo.' : 'La tarea se fijó al equipo.');
      await onActionComplete?.();
    } catch (error) {
      setErrorMessage(error.message || 'No se pudo actualizar la tarea.');
    } finally {
      setLoadingAction('');
    }
  };

  const handleCancel = () => {
    clearMessages();
    setShowForceCreateAlert(false);

    if (isFinalized) {
      setErrorMessage('No se puede cancelar una tarea finalizada.');
      return;
    }

    setShowCancelForm(true);
  };

  const handleCancelFormSuccess = async () => {
    setShowCancelForm(false);
    setSuccessMessage('La tarea se canceló correctamente.');
    await onActionComplete?.();
  };

  const handleReactivate = async (forceCreate = false) => {
    clearMessages();
    setShowForceCreateAlert(false);

    if (!isCancelled) {
      setErrorMessage('Solo se puede reactivar una tarea cancelada.');
      return;
    }

    if (!reactivationReason.trim()) {
      setErrorMessage('Debes indicar un motivo de reactivación.');
      return;
    }

    if (isBeforeToday(reactivationDate)) {
      setErrorMessage('La nueva fecha no puede ser menor al día actual.');
      return;
    }

    setLoadingAction('reactivate');

    try {
      await taskService.reactivateTask(task.id, {
        reason: reactivationReason.trim(),
        newScheduledDate: reactivationDate || null,
        forceCreate
      });

      setSuccessMessage('Tarea reactivada correctamente.');
      await onActionComplete?.();
    } catch (error) {
      if (!forceCreate && error?.message?.includes('inhabilitado')) {
        setShowForceCreateAlert(true);
        return;
      }

      setErrorMessage(error.message || 'No se pudo reactivar la tarea.');
    } finally {
      setLoadingAction('');
    }
  };

  return (
    <>
      <section className="rounded-3xl border border-line bg-surface/80 p-6 shadow-lg shadow-black/10">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-xl bg-surface-2 p-2 text-content-muted">
            <Lightning size={20} />
          </div>
          <h2 className="text-lg font-semibold text-content">Acciones Rápidas</h2>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleReset}
            disabled={loadingAction !== '' || isCancelled || isFinalized}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber px-4 py-2.5 text-sm font-semibold text-on-amber transition-colors hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowsClockwise size={16} />
            {loadingAction === 'reset' ? 'Reiniciando...' : 'Reiniciar (Pendiente)'}
          </button>

          <button
            type="button"
            onClick={handleToggleFixedTeam}
            disabled={loadingAction !== '' || !hasTeam}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber/16 hover:bg-amber/24 border border-amber/60 px-4 py-2.5 text-sm font-semibold text-accent-text transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isFixedTeam ? <PushPinSlash size={16} /> : <PushPin size={16} />}
            {loadingAction === 'fixedTeam'
              ? 'Guardando...'
              : isFixedTeam ? 'Desfijar al equipo' : 'Fijar al equipo'}
          </button>
          {!hasTeam && (
            <p className="text-xs text-content-muted">Fijar al equipo solo aplica a tareas con equipo asignado.</p>
          )}

          <div className="space-y-2 rounded-2xl border border-line/80 bg-bg/40 p-3">
            <label htmlFor="quick-reactivate-reason" className="text-xs font-medium text-content-muted">
              Motivo de reactivación
            </label>
            <textarea
              id="quick-reactivate-reason"
              value={reactivationReason}
              onChange={(event) => setReactivationReason(event.target.value)}
              rows={2}
              className="w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none transition-colors focus:border-amber"
              disabled={!canEditReactivationFields}
            />

            <p className="text-xs font-medium text-content-muted">
              Nueva fecha
            </p>
            <DateTimePickerField
              value={reactivationDate}
              onChange={setReactivationDate}
              minDate={minReactivationDate}
              placeholder="dd/mm/aaaa --:--"
              disabled={!canEditReactivationFields}
            />

            <button
              type="button"
              onClick={() => handleReactivate()}
              disabled={loadingAction !== '' || !isCancelled || showForceCreateAlert}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-success/16 hover:bg-success/24 border border-success/60 px-4 py-2.5 text-sm font-semibold text-success transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowsClockwise size={16} />
              {loadingAction === 'reactivate' ? 'Reactivando...' : 'Reactivar'}
            </button>
            {!isCancelled && (
              <p className="text-xs text-content-muted">La reactivación solo aplica a tareas en estado cancelada.</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={loadingAction !== '' || isCancelled || isFinalized}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-content transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <XCircle size={16} />
            {loadingAction === 'cancel' ? 'Cancelando...' : 'Cancelar'}
          </button>
          {isFinalized && (
            <p className="text-xs text-content-muted">La cancelación solo aplica a tareas no finalizadas.</p>
          )}
        </div>

        {errorMessage && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-700/70 bg-red-950/30 px-3 py-2 text-sm text-red-200">
            <WarningCircle size={16} className="mt-0.5" />
            <p>{errorMessage}</p>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 rounded-xl border border-emerald-700/70 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-200">
            {successMessage}
          </div>
        )}
      </section>
      {showForceCreateAlert && (
        <Alerts title="Aviso" onClose={() => setShowForceCreateAlert(false)} width="max-w-md">
          <h3 className="text-md">
            La reactivación seleccionada se encuentra inhabilitada para la fecha{' '}
            {reactivationDate ? new Date(reactivationDate).toLocaleDateString('es-AR') : 'indicada'}.
            ¿Quieres continuar e ignorar esta inhabilitación?
          </h3>

          <div className="flex justify-end space-x-3 border-t border-line pt-6">
            <button
              type="button"
              onClick={() => setShowForceCreateAlert(false)}
              className="rounded-md bg-surface-2 px-4 py-2 text-content hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => handleReactivate(true)}
              className="rounded-md bg-success/16 hover:bg-success/24 border border-success/60 px-6 py-2 text-success transition-colors"
            >
              Continuar reactivación
            </button>
          </div>
        </Alerts>
      )}
      {showCancelForm && (
        <DynamicForm
          task={task}
          formType="cancelacion"
          title={`Cancelar tarea: ${task?.title || ''}`}
          submitButtonText="Confirmar cancelación"
          onClose={() => setShowCancelForm(false)}
          onSuccess={handleCancelFormSuccess}
        />
      )}
    </>
  );
}

export default TaskQuickActionsCard;